import { useState } from "react";
import { Link } from "react-router-dom";
import apiUtils from "../utils/apiUtils";
import usePageMeta from "../hooks/usePageMeta";

export default function ForgotPassword() {
  usePageMeta({
    title: "Reset Password · RepCheck",
    description: "Reset your RepCheck account password.",
  });

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await apiUtils.post("/api/users/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-card flex flex-col">
      {/* Top bar */}
      <div className="flex justify-between items-center px-6 sm:px-10 pt-6 pb-2">
        <span className="flex items-center text-[2rem] font-bold font-roboto">
          <img src="/favicon.svg" alt="RepCheck logo" width="32" height="32" className="h-8 w-8 mr-2" />
          RepCheck
        </span>
        <span className="text-lg font-semibold font-roboto">
          <Link to="/login">Sign in</Link>
        </span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <div className="bg-gradient-to-b from-muted via-card to-accent border border-input rounded-2xl shadow-lg px-6 sm:px-10 py-12 flex flex-col items-center max-w-md w-full">
          {done ? (
            <>
              <h1 className="text-2xl font-bold font-roboto text-center mb-2">Check your email</h1>
              <p className="text-base text-foreground text-center font-roboto">
                If an account exists for <span className="font-medium">{email}</span>, we've sent a link to
                reset your password. The link expires in 1 hour.
              </p>
              <Link to="/login" className="mt-6 font-roboto font-medium text-brand-600 hover:text-brand-700">
                Back to sign in
              </Link>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold font-roboto italic text-center mb-1">Forgot password?</h1>
              <p className="text-base text-foreground text-center font-roboto mb-6">
                Enter your email and we'll send you a link. This works whether you're setting your password for the first time or resetting it.
              </p>
              {error && (
                <div className="mb-4 w-full rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="w-full space-y-4">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full rounded-xl border border-input px-4 py-3 font-roboto focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full rounded-full py-3 font-semibold text-white transition-colors ${
                    submitting ? "cursor-not-allowed bg-neutral-400" : "bg-brand-600 hover:bg-brand-600"
                  }`}
                >
                  {submitting ? "Sending…" : "Send reset link"}
                </button>
              </form>
              <div className="mt-6 text-sm font-roboto">
                <Link to="/login" className="text-foreground hover:text-brand-600">
                  Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
